/**
 * useAccordionState - Hook for managing accordion expand/collapse state
 *
 * Features:
 * - All accordions CLOSED by default (empty Set)
 * - Toggle individual groups
 * - Expand/collapse all
 * - Check if specific group is expanded
 * - Optional DB persistence via viewCode parameter
 *
 * @param {Object} options
 * @param {string[]} options.defaultExpanded - Array of group IDs to expand by default (empty = all closed)
 * @param {string} options.viewCode - If provided, state is persisted to DB (ui_accordion_state table)
 * @returns {Object} Accordion state and handlers
 */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { accordionStateAPI } from '../api.js';

export function useAccordionState({ defaultExpanded = [], viewCode = null } = {}) {
  const [expandedGroups, setExpandedGroups] = useState(() => new Set(defaultExpanded));
  const [isLoading, setIsLoading] = useState(!!viewCode);
  const initialLoadDone = useRef(false);

  // Load state from DB when viewCode is provided
  useEffect(() => {
    if (!viewCode || initialLoadDone.current) return;

    const loadState = async () => {
      try {
        const response = await accordionStateAPI.getForView(viewCode);
        if (response.ok && response.data) {
          const expandedKeys = Object.keys(response.data).filter(k => response.data[k]);
          setExpandedGroups(new Set(expandedKeys));
        }
      } catch (err) {
        console.error('Failed to load accordion state:', err);
      } finally {
        setIsLoading(false);
        initialLoadDone.current = true;
      }
    };

    loadState();
  }, [viewCode]);

  // Persist state to DB
  const persistState = useCallback(async (groupId, isExpanded) => {
    if (!viewCode) return;
    try {
      await accordionStateAPI.set(viewCode, groupId, isExpanded);
    } catch (err) {
      console.error('Failed to persist accordion state:', err);
    }
  }, [viewCode]);

  /**
   * Toggle a single group's expanded state
   * @param {string} groupId - The group ID to toggle
   */
  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      const willExpand = !next.has(groupId);
      if (willExpand) {
        next.add(groupId);
      } else {
        next.delete(groupId);
      }
      // Persist to DB if viewCode is set
      persistState(groupId, willExpand);
      return next;
    });
  }, [persistState]);

  /**
   * Check if a group is currently expanded
   * @param {string} groupId - The group ID to check
   * @returns {boolean}
   */
  const isExpanded = useCallback((groupId) => {
    return expandedGroups.has(groupId);
  }, [expandedGroups]);

  /**
   * Expand all groups
   * @param {string[]} allGroupIds - Array of all group IDs to expand
   */
  const expandAll = useCallback((allGroupIds) => {
    setExpandedGroups(new Set(allGroupIds));
  }, []);

  /**
   * Collapse all groups
   */
  const collapseAll = useCallback(() => {
    setExpandedGroups(new Set());
  }, []);

  /**
   * Set specific groups as expanded (replaces current state)
   * @param {string[]} groupIds - Array of group IDs to set as expanded
   */
  const setExpanded = useCallback((groupIds) => {
    setExpandedGroups(new Set(groupIds));
  }, []);

  /**
   * Expand a single group without toggling
   * @param {string} groupId - The group ID to expand
   */
  const expand = useCallback((groupId) => {
    setExpandedGroups(prev => new Set([...prev, groupId]));
  }, []);

  /**
   * Collapse a single group without toggling
   * @param {string} groupId - The group ID to collapse
   */
  const collapse = useCallback((groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
  }, []);

  // Memoize return object to prevent unnecessary re-renders
  const result = useMemo(() => ({
    expandedGroups,
    toggleGroup,
    isExpanded,
    expandAll,
    collapseAll,
    setExpanded,
    expand,
    collapse,
    // Convenience: number of currently expanded groups
    expandedCount: expandedGroups.size,
    // Loading state (true while fetching from DB)
    isLoading,
  }), [expandedGroups, toggleGroup, isExpanded, expandAll, collapseAll, setExpanded, expand, collapse, isLoading]);

  return result;
}

export default useAccordionState;
